// ccp/src/hooks/useCoaching.js
//
// Copilot coaching v1 — evaluates the two named triggers against the live
// contact, enforces the 30 s per-call floor, calls the knktr-agent-coaching
// Lambda for ONE sentence, and exposes the latest cue for the read-only
// panel. Trigger logic and debounce are in lib/coachingTriggers.js (pure);
// this hook only schedules evaluations and owns the fetch.
//
// It never touches the copilot Q&A panel, its endpoint or its state.
//
// D35 (2026-09-16): the transcript the triggers see is the Phase 3 feed from
// TranscriptContext (`useTranscript().lines`), passed in via coachingView().
// v1 as deployed evaluated `display` alone, whose `transcript` is a permanent
// [] on live calls (connectData.js), so trigger 2 could never fire — proven
// on the first real call. Do not read display.transcript here again.
import { useEffect, useRef, useState, useCallback } from 'react';
import { useContact } from '../context/ContactContext';
import { useTranscript } from '../context/TranscriptContext';
import { apiHeaders } from '../lib/consoleApi';
import { evaluateTriggers, markFired, newCoachingState, coachingView } from '../lib/coachingTriggers';

const COACHING_URL = import.meta.env.VITE_COACHING_URL || '';

// Per-contact state survives a remount of the panel mid-call (e.g. rail
// re-render); it is dropped when the contact changes.
const stateByContact = new Map();
function stateFor(contactId) {
  if (!stateByContact.has(contactId)) {
    stateByContact.clear();                       // one live contact at a time
    stateByContact.set(contactId, newCoachingState());
  }
  return stateByContact.get(contactId);
}

export default function useCoaching() {
  const { display } = useContact();
  const { lines } = useTranscript();                 // D35: the Phase 3 feed, the only transcript source
  const contactId = display?.meta?.contactId ?? null;
  const kbId = display?.knowledgeBaseId ?? null;
  const transcriptLen = lines.length;
  const escTrigger = display?.escalation?.trigger ?? null;

  const [latest, setLatest] = useState(null);     // { trigger, label, suggestion, at }
  const [history, setHistory] = useState([]);     // earlier cues, newest first, max 3
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);            // bumped when the 30 s floor expires
  const inFlight = useRef(false);
  const timer = useRef(null);
  const displayRef = useRef(display);
  displayRef.current = display;
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // New contact → clear the panel.
  useEffect(() => {
    setLatest(null); setHistory([]); setError(null); setPending(false);
  }, [contactId]);

  const fire = useCallback(async (trigger, key, context) => {
    if (!contactId || inFlight.current) return;
    const state = stateFor(contactId);
    markFired(state, trigger, key);               // counts against the floor even if the call fails
    // 23 Oct: a fixed cue (personal details) needs no model call.
    if (trigger.staticCue) {
      const cue = { trigger: trigger.id, label: trigger.label, suggestion: trigger.staticCue, at: Date.now() };
      setLatest(prev => {
        if (prev) setHistory(h => [prev, ...h].slice(0, 3));
        return cue;
      });
      return;
    }
    if (!COACHING_URL) { setError('Coaching endpoint not configured (VITE_COACHING_URL).'); return; }
    if (!kbId) { setError('No knowledge base is linked to this contact.'); return; }
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      const res = await fetch(COACHING_URL, {
        method: 'POST',
        headers: await apiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ trigger: trigger.id, knowledgeBaseId: kbId, contactId, context }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || `Coaching failed (HTTP ${res.status}).`);
      if (data.blocked || !data.suggestion) return;   // guardrail intervened: show nothing
      const cue = { trigger: trigger.id, label: data.label || trigger.label, suggestion: data.suggestion, at: Date.now() };
      setLatest(prev => {
        if (prev) setHistory(h => [prev, ...h].slice(0, 3));
        return cue;
      });
    } catch (err) {
      setError(err?.message || 'Coaching unavailable.');
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, [contactId, kbId]);

  // Evaluate whenever a trigger signal changes (escalation context on connect,
  // a new feed line) and again when the floor expires. `display` and the feed
  // are read through refs so unrelated renders do not re-run this.
  useEffect(() => {
    clearTimeout(timer.current);
    const current = displayRef.current;
    if (!contactId || !current) return undefined;
    const decision = evaluateTriggers(coachingView(current, linesRef.current), stateFor(contactId));
    if (!decision) return undefined;
    if (decision.waitMs != null) {
      timer.current = setTimeout(() => setTick(t => t + 1), decision.waitMs + 50);
      return () => clearTimeout(timer.current);
    }
    fire(decision.trigger, decision.key, decision.context);
    return () => clearTimeout(timer.current);
  }, [contactId, escTrigger, transcriptLen, tick, fire]);

  return { latest, history, pending, error, enabled: Boolean(COACHING_URL) };
}
